import { useMemo } from "react";
import { useSelector } from "react-redux";

import config from "#config";
import { getActiveStoreId } from "src/redux/selectors/activeStore";
import { useIsBridgeEnabled } from "src/api/bridge/useIsBridgeEnabled";
import { useSSEQuery } from "src/api/bridge/useSSEQuery";

// Bridge data structure from SSE response
export type BridgeOrderData = {
    mongoID: string;
    check: {
        key: string;
        status: string;
        subtotal: { amount: number; currency: string };
        total: { amount: number; currency: string };
    };
    chits: Array<{
        key: string;
        status: Record<string, string>;
    }>;
    payments: Array<{
        key: string;
        status: Record<string, string>;
    }>;
    tickets: Array<{
        key: string;
        status: Record<string, string>;
    }>;
    order: {
        createdAt: string;
        key: string;
        orderNumber: string;
    };
};

// Bridge SSE response structure
export type BridgeResponse = {
    errors: string[] | null;
    result: BridgeOrderData[];
};

/**
 * Shared hook to fetch bridge data using SSE for order IDs
 */
export const useBridgeOrdersData = (orderIds: string) => {
    // Get store ID for bridge URL
    const storeId = useSelector(getActiveStoreId);
    const isBridgeEnabled = useIsBridgeEnabled();

    const bridgeEndpoint = `${config.bridgeUrl(storeId)}/api/legacy-purchase-data`;

    // Memoize queryParams to prevent object recreation
    const params = useMemo(() => orderIds.split(","), [orderIds]);

    // Check if we should enable the query
    const hasOrders = orderIds.length > 0;

    const {
        data: bridgeResponse,
        error: bridgeError,
        isLoading: bridgeLoading,
    } = useSSEQuery<BridgeResponse>({
        endpoint: bridgeEndpoint,
        enabled: isBridgeEnabled && hasOrders && !!storeId && !!bridgeEndpoint,
        params,
    });

    // If no orders, return empty result immediately (settled state)
    if (!hasOrders) {
        return {
            bridgeResponse: { errors: null, result: [] } as BridgeResponse,
            bridgeError: null,
            bridgeLoading: false,
        };
    }

    return {
        bridgeResponse,
        bridgeError,
        bridgeLoading: isBridgeEnabled
            ? bridgeLoading || bridgeResponse === undefined
            : false,
    };
};
